/**
 * "Share to X" prompt after a buy / taking the lead.
 *
 * The card is rendered in-app from utils/shareCardSvg.ts (holo HUD palette, NT3D mark, the
 * player's faction holo emblem, abbreviated stats). The share-cards worker uses the same
 * generator (workers/share-cards/src/cardSvg.ts), so the linked image matches this preview.
 */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getFactionByContractId } from '../config/factions';
import { abbreviate, buildShareCardSvg, CARD_FACTIONS } from '../utils/shareCardSvg';

export const SHARE_CARDS_URL = import.meta.env.VITE_SHARE_CARDS_URL || 'https://nt3d-share-cards.wurkit.workers.dev';
export const GAME_SHARE_URL = 'https://nt3d.fun';

export interface ShareInfo {
  account: string | null;
  /** keys bought in this tx (display string or number) */
  keys: string;
  /** test FRAME spent, whole tokens (display string, commas allowed) */
  amount: string;
  /** contract faction id 1-8 (the faction the buy was actually recorded under) */
  faction: number;
  tookLead: boolean;
}

const num = (s: string) => Number(String(s).replace(/[^0-9.]/g, '')) || 0;

export function buildShareCardUrl(i: ShareInfo) {
  const p = new URLSearchParams({
    player: i.account || '0x0000000000000000000000000000000000000000',
    keys: String(Math.round(num(i.keys))),
    spent: String(num(i.amount)),
    faction: String(i.faction || 1),
    lead: i.tookLead ? '1' : '0',
  });
  return `${SHARE_CARDS_URL}/purchase?${p.toString()}`;
}

export function buildTweetIntent(i: ShareInfo) {
  const faction = getFactionByContractId(i.faction)?.name;
  const keys = abbreviate(num(i.keys));
  const text = i.tookLead
    ? `I just took the lead on No Tomorrow 3D${faction ? ` for team ${faction}` : ''}. Last buyer when the timer hits zero wins the pot. Come take it from me.`
    : `Just grabbed ${keys} key${keys === '1' ? '' : 's'} on No Tomorrow 3D${faction ? ` (${faction})` : ''}. Every key adds 30s, last buyer wins.`;
  return `https://x.com/intent/tweet?text=${encodeURIComponent(text + ' Free testnet demo, test FRAME only. #NT3D')}&url=${encodeURIComponent(GAME_SHARE_URL)}`;
}

const dataUriCache = new Map<string, Promise<string | undefined>>();
function toDataUri(path: string): Promise<string | undefined> {
  if (!dataUriCache.has(path)) {
    dataUriCache.set(path, fetch(path)
      .then(r => (r.ok ? r.blob() : Promise.reject(r.status)))
      .then(b => new Promise<string>((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.onerror = rej; fr.readAsDataURL(b); }))
      .catch(() => undefined));
  }
  return dataUriCache.get(path)!;
}

/** Card SVG with inlined emblem + mark, ready for <img>, download and canvas export. */
export function useShareCardSvg(info: ShareInfo | null) {
  const [assets, setAssets] = useState<{ logo?: string; mark?: string }>({});
  const faction = info?.faction || 1;
  useEffect(() => {
    if (!info) return;
    let live = true;
    const f = CARD_FACTIONS[faction] || CARD_FACTIONS[1];
    Promise.all([toDataUri(`/factions/holo/${f.file}.svg`), toDataUri('/nt3d-mark.webp')])
      .then(([logo, mark]) => { if (live) setAssets({ logo, mark }); });
    return () => { live = false; };
  }, [info, faction]);
  return useMemo(() => info ? buildShareCardSvg({
    player: info.account || '',
    keys: num(info.keys),
    spent: num(info.amount),
    faction,
    tookLead: info.tookLead,
    factionLogo: assets.logo,
    mark: assets.mark,
  }) : '', [info, faction, assets]);
}

async function svgToPng(svg: string): Promise<Blob | null> {
  const img = new Image();
  img.decoding = 'async';
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await img.decode().catch(() => undefined);
  const c = document.createElement('canvas');
  c.width = 1200; c.height = 630;
  const ctx = c.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, 1200, 630);
  return new Promise(res => c.toBlob(b => res(b), 'image/png'));
}

interface Props { info: ShareInfo | null; onClose: () => void; }

export default function SharePrompt({ info, onClose }: Props) {
  const svg = useShareCardSvg(info);
  const [copied, setCopied] = useState(false);
  if (!info) return null;
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  const f = CARD_FACTIONS[info.faction] || CARD_FACTIONS[1];

  const download = async () => {
    const png = await svgToPng(svg);
    const a = document.createElement('a');
    a.href = png ? URL.createObjectURL(png) : src;
    a.download = `nt3d-${f.file}-${info.tookLead ? 'lead' : 'keys'}.${png ? 'png' : 'svg'}`;
    a.click();
    if (png) setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  return createPortal(
    <div className="share-prompt" role="dialog" aria-modal="false" aria-label={info.tookLead ? "You're in the lead" : 'Keys acquired'}>
      <div className="sp-card">
        <div className="sp-head">
          <div className="sp-brand">
            <img src="/nt3d-mark.webp" alt="" width={28} height={28} />
            <div>
              <div className="sp-title">{info.tookLead ? "You're in the lead!" : 'Keys acquired!'}</div>
              <div className="sp-sub">Resistance member · {f.name} cell</div>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="sp-x">&times;</button>
        </div>
        <img src={src} alt={`Share card: ${abbreviate(num(info.keys))} keys for ${f.name}`} className="sp-img" width={1200} height={630} />
        <dl className="sp-stats">
          <div><dt>Keys</dt><dd>{abbreviate(num(info.keys))}</dd></div>
          <div><dt>Spent</dt><dd>{abbreviate(num(info.amount))}<small>test FRAME</small></dd></div>
          <div><dt>Faction</dt><dd className="sp-f"><img src={`/factions/holo/${f.file}.svg`} alt="" width={20} height={20} />{f.name}</dd></div>
        </dl>
        <div className="sp-actions">
          <a href={buildTweetIntent(info)} target="_blank" rel="noopener noreferrer" onClick={onClose} className="sp-btn pri">Share to X</a>
          <button type="button" onClick={download} className="sp-btn">Download image</button>
          <button
            type="button"
            onClick={() => { navigator.clipboard?.writeText(buildShareCardUrl(info)).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); }}
            className="sp-btn"
          >
            {copied ? 'Copied' : 'Copy card link'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
